import {
  Input,
  Modal,
  NewButton,
  NewButtonVariant,
  SelectInput,
} from "@visiblethread/react-components";
import Flex, { DIRECTION_CLASS, GAP_CLASS } from "components/Flex";
import { ALIGN_CLASS } from "components/Flex/Flex";
import { modalTypes } from "constants/types";
import { func, node, shape, string } from "prop-types";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { buildArchiveStatusActions } from "slices/archiveStatuses/archiveStatusesActions";
import { buildArchiveStatusSelectors } from "slices/archiveStatuses/archiveStatusesSelectors";

import classes from "./ArchiveEntityModal.module.scss";

const NOTE_MAX_LENGTH = 1000;

const slotShape = shape({
  entityType: string.isRequired,
  label: string.isRequired,
});

const useSlotStatuses = (entityType) => {
  const dispatch = useDispatch();
  const { selectItems } = buildArchiveStatusSelectors(entityType);
  const statuses = useSelector(selectItems);

  useEffect(() => {
    if (entityType) {
      dispatch(buildArchiveStatusActions(entityType).fetchItems());
    }
  }, [entityType, dispatch]);

  return statuses;
};

const ArchiveEntityModal = ({
  primarySlot,
  optionalSlot,
  onConfirm,
  entityName,
  cascadeWarning,
}) => {
  const dispatch = useDispatch();
  const [primaryStatusId, setPrimaryStatusId] = useState("");
  const [scope, setScope] = useState(null);
  const [optionalStatusId, setOptionalStatusId] = useState("");
  const [note, setNote] = useState("");
  const optionalEnabled = scope === true;

  const primaryStatuses = useSlotStatuses(primarySlot.entityType);
  const optionalStatuses = useSlotStatuses(optionalEnabled ? optionalSlot?.entityType : null);

  const primaryLabel = `${primarySlot.label} status`;
  const optionalLabel = optionalSlot ? `${optionalSlot.label} status` : "";
  const primaryNoun = primarySlot.label.toLowerCase();
  const optionalNoun = optionalSlot ? optionalSlot.label.toLowerCase() : "";

  const handleClose = () => dispatch(hideModal(modalTypes.ARCHIVE_ENTITY_MODAL));

  const handleSelectPrimaryOnly = () => {
    setScope(false);
    setOptionalStatusId("");
  };

  const handleSelectWithOptional = () => setScope(true);

  const handleConfirm = () => {
    const statusIdsBySlot = { primary: Number.parseInt(primaryStatusId, 10) };
    if (optionalEnabled) {
      statusIdsBySlot.optional = Number.parseInt(optionalStatusId, 10);
    }
    const trimmedNote = note.trim();
    onConfirm({ statusIdsBySlot, note: trimmedNote === "" ? undefined : trimmedNote });
    handleClose();
  };

  const optionalScopeSatisfied =
    !optionalSlot ||
    scope === false ||
    (scope === true && optionalStatusId !== "");
  const canConfirm = primaryStatusId !== "" && optionalScopeSatisfied;

  return (
    <Modal show centered backdrop onDismiss={handleClose} className="fade-in">
      <Modal.Header>Archive</Modal.Header>
      <Modal.Body className={classes.archive_entity_modal}>
        <Flex
          className={classes.archive_entity_modal__intro}
          direction={DIRECTION_CLASS.column}
          gap={GAP_CLASS.GAP_200}
          alignItems={ALIGN_CLASS["flex-start"]}
        >
          <p className={classes.archive_entity_modal__helper}>
            You are about to archive <strong>{entityName}.</strong>
          </p>
          {cascadeWarning && (
            <ul className={classes.archive_entity_modal__cascade}>
              <li>{cascadeWarning}</li>
            </ul>
          )}
        </Flex>

        <div className={classes.archive_entity_modal__field}>
          <label className={classes.archive_entity_modal__field_label} htmlFor="archive-entity-primary-status">
            {primaryLabel} *
          </label>
          <div>
            <SelectInput
              id="archive-entity-primary-status"
              optionListClassName={classes.archive_entity_modal__select_options}
              value={primaryStatusId}
              options={primaryStatuses.map((status) => ({ value: status.id, label: status.name }))}
              placeholder={primaryLabel}
              onChange={setPrimaryStatusId}
            />
          </div>
        </div>

        {optionalSlot && (
          <Flex
            className={classes.archive_entity_modal__optional_slot}
            direction={DIRECTION_CLASS.column}
            gap={GAP_CLASS.GAP_200}
            alignItems={ALIGN_CLASS.stretch}
          >
            <Flex
              className={classes.archive_entity_modal__options}
              direction={DIRECTION_CLASS.column}
              gap={GAP_CLASS.GAP_200}
              alignItems={ALIGN_CLASS["flex-start"]}
            >
              <label className={classes.archive_entity_modal__options__option}>
                <input
                  type="radio"
                  name="archive-entity-scope"
                  checked={scope === false}
                  onChange={handleSelectPrimaryOnly}
                />
                {`Archive this ${primaryNoun} only`}
              </label>
              <label className={classes.archive_entity_modal__options__option}>
                <input
                  type="radio"
                  name="archive-entity-scope"
                  checked={scope === true}
                  onChange={handleSelectWithOptional}
                />
                {`Archive this ${primaryNoun} and the associated ${optionalNoun}`}
              </label>
            </Flex>

            {optionalEnabled && (
              <div className={classes.archive_entity_modal__field}>
                <label
                  className={classes.archive_entity_modal__field_label}
                  htmlFor="archive-entity-optional-status"
                >
                  {optionalLabel} *
                </label>
                <div>
                  <SelectInput
                    id="archive-entity-optional-status"
                    optionListClassName={classes.archive_entity_modal__select_options}
                    value={optionalStatusId}
                    options={optionalStatuses.map((status) => ({ value: status.id, label: status.name }))}
                    placeholder={optionalLabel}
                    onChange={setOptionalStatusId}
                  />
                </div>
              </div>
            )}
          </Flex>
        )}

        <div className={classes.archive_entity_modal__field}>
          <label className={classes.archive_entity_modal__field_label} htmlFor="archive-entity-note">
            Note <span className={classes.archive_entity_modal__optional}>(optional)</span>
          </label>
          <Input
            id="archive-entity-note"
            name="archive-entity-note"
            value={note}
            onChange={(value) => setNote(value.slice(0, NOTE_MAX_LENGTH))}
            placeholder="Context for the capture review — competitor, price delta, agency signal"
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <NewButton variant={NewButtonVariant.l3} onClick={handleClose}>
          Cancel
        </NewButton>
        <NewButton variant={NewButtonVariant.l1} disabled={!canConfirm} onClick={handleConfirm}>
          Archive
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

ArchiveEntityModal.propTypes = {
  primarySlot: slotShape.isRequired,
  optionalSlot: slotShape,
  onConfirm: func.isRequired,
  entityName: string.isRequired,
  cascadeWarning: node,
};

export default ArchiveEntityModal;
